import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { configureForm } from '@thinkmill/pragmatic-forms';

import { encode } from 'inside-out/managers/formManager';
import { DropZone } from 'inside-out/components/shared/dropZone';

import { Link } from 'inside-out/components/shared/link';
import { HeaderManager } from 'inside-out/managers/headerManager';

import {
	wrapperStyle,
	innerWrapperStyle,
	titleStyle,
	bodyStyle,
	messageTextareaStyle,
} from 'inside-out/page-styles/employment/detail';

import {
	detailSideBarLayout,
	contentSection,
	formSection,
	button,
	aqua,
	pink,
} from 'inside-out/global-styles';

// Gatsby requires a specific export format on Templates.
export default class EmploymentSubmitPage extends React.PureComponent {
	constructor() {
		super();
		this.state = { hasSubmitted: false };
	}

	componentDidMount = () => {
		HeaderManager.headerProperties = {};
	};

	EmploymentSubmitForm = configureForm({
		initFields: () => ({
			'form-name': 'submit-employment-opportunity',
			botField: '',
			jobPosition: '',
			employer: '',
			logo: null,
			location: '',
			hoursPerWeek: '',
			aboutTheRole: '',
			linkToEmploymentPositionPage: '',
			recruitmentClosingDate: '',
			flyer: null,
			firstName: '',
			lastName: '',
			email: '',
			phoneNumberPrefix: '+61',
			phoneNumber: '',
		}),
		validate: ({
			jobPosition,
			employer,
			location,
			hoursPerWeek,
			linkToEmploymentPositionPage,
			recruitmentClosingDate,
			firstName,
			lastName,
			email,
			phoneNumberPrefix,
			phoneNumber,
		}) => {
			const errors = {};

			if (!jobPosition) errors.jobPosition = 'Please enter a Job Position';
			if (!employer) errors.employer = 'Please enter an employer';
			if (!location) errors.location = 'Please enter a location';
			if (!hoursPerWeek) errors.hoursPerWeek = 'Please enter hours per week';
			if (!linkToEmploymentPositionPage)
				errors.linkToEmploymentPositionPage = 'Please a link to the employment position page';
			if (!recruitmentClosingDate)
				errors.recruitmentClosingDate = 'Please enter Closing Date for Recruitment';
			if (!firstName) errors.firstName = 'Please enter your First Name';
			if (!lastName) errors.lastName = 'Please enter your Last Name';
			if (!email) errors.email = 'Please enter an Email Address';
			if (!phoneNumberPrefix) errors.phoneNumberPrefix = 'Please enter a phone prefix';
			if (!phoneNumber) errors.phoneNumber = 'Please enter a phone number';

			return errors;
		},
		submit: async formData => {
			try {
				await fetch('/', {
					method: 'POST',
					body: encode(formData),
				});

				this.setState({ hasSubmitted: true });
			} catch (error) {
				console.error(error);
			}
		},
	})(({ form }) => (
		<form.Form netlify="true" name="submit-employment-opportunity" netlify-honeypot="botField">
			<div className={formSection.wrapper}>
				<p style={{ display: 'none' }}>
					<label>
						Don’t fill this out if you&apos;re human:
						<input {...form.getInputProps({ name: 'botField', type: 'text' })} />
					</label>
				</p>
				<div aria-live="assertive">
					{/* Errors that come back from the server */}
					{form.submitError && (
						<p className={formSection.validationMessage}>Error: {form.submitError}</p>
					)}
					{/* Field Validation Errors */}
					{form.hasErrors && (
						<ul className={formSection.validationSummary}>
							{Object.keys(form.errors).map(key => (
								<li key={key} className={formSection.validationMessage}>{`${form.errors[key]}`}</li>
							))}
						</ul>
					)}
				</div>

				<fieldset className={formSection.fieldset}>
					<legend>About the position</legend>
					<div className={formSection.field}>
						{form.errors['jobPosition'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['jobPosition']}</p>
						)}
						<label htmlFor="jobPosition">Job Position / Title</label>
						<input
							id="jobPosition"
							{...form.getInputProps({ name: 'jobPosition', type: 'text' })}
						/>
					</div>
					<div className={formSection.field}>
						{form.errors['employer'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['employer']}</p>
						)}
						<label htmlFor="employer">Employer</label>
						<input id="employer" {...form.getInputProps({ name: 'employer', type: 'text' })} />
					</div>

					<div className={formSection.field}>
						<DropZone
							className={formSection.dropZoneField.default}
							{...form.getInputProps({ name: 'logo', type: 'file' })}
						>
							{(file, clearFile) => {
								if (file) {
									return (
										<p>
											<button onClick={clearFile}>Clear</button>
											<span>{file.name}</span>
										</p>
									);
								} else {
									return (
										<p>
											<span>Drop your company logo here or</span>
											<span className={button(aqua)}>Choose a file to upload</span>
										</p>
									);
								}
							}}
						</DropZone>
					</div>

					<div className={formSection.field}>
						{form.errors['location'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['location']}</p>
						)}
						<label htmlFor="location">Location</label>
						<input id="location" {...form.getInputProps({ name: 'location', type: 'text' })} />
					</div>
					<div className={formSection.field}>
						{form.errors['hoursPerWeek'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['hoursPerWeek']}</p>
						)}
						<label htmlFor="hoursPerWeek">Hours per week</label>
						<input
							id="hoursPerWeek"
							{...form.getInputProps({ name: 'hoursPerWeek', type: 'number' })}
						/>
					</div>
					<div className={formSection.field}>
						<label htmlFor="aboutTheRole">About the Role (optional)</label>
						<textarea
							id="aboutTheRole"
							className={messageTextareaStyle}
							{...form.getInputProps({ name: 'aboutTheRole' })}
						/>
					</div>
					<div className={formSection.field}>
						<label htmlFor="selectionCriteria">Selection Criteria (optional)</label>
						<textarea
							id="selectionCriteria"
							className={messageTextareaStyle}
							{...form.getInputProps({ name: 'selectionCriteria' })}
						/>
					</div>
					<div className={formSection.field}>
						{form.errors['linkToEmploymentPositionPage'] && (
							<p className={formSection.validationFieldMessage}>
								{form.errors['linkToEmploymentPositionPage']}
							</p>
						)}
						<label htmlFor="linkToEmploymentPositionPage">Link to employment position page</label>
						<input
							id="linkToEmploymentPositionPage"
							{...form.getInputProps({ name: 'linkToEmploymentPositionPage', type: 'text' })}
						/>
					</div>
					<div className={formSection.field}>
						{form.errors['recruitmentClosingDate'] && (
							<p className={formSection.validationFieldMessage}>
								{form.errors['recruitmentClosingDate']}
							</p>
						)}
						<label htmlFor="recruitmentClosingDate">Application closing date</label>
						<input
							id="recruitmentClosingDate"
							placeholder="dd/mm/yyyy"
							{...form.getInputProps({ name: 'recruitmentClosingDate', type: 'text' })}
						/>
					</div>
					<div className={formSection.field}>
						<DropZone
							className={formSection.dropZoneField.default}
							{...form.getInputProps({ name: 'flyer', type: 'file' })}
						>
							{(file, clearFile) => {
								if (file) {
									return (
										<p>
											<button onClick={clearFile}>Clear</button>
											<span>{file.name}</span>
										</p>
									);
								} else {
									return (
										<p>
											<span>Drop detailed flyer, PDF, position description files here or</span>
											<span className={button(aqua)}>Choose a file to upload</span>
										</p>
									);
								}
							}}
						</DropZone>
					</div>
				</fieldset>

				<fieldset className={formSection.fieldset}>
					<legend>Contact</legend>

					<div className={formSection.halfWidthWrapper}>
						<div className={formSection.field}>
							{form.errors['firstName'] && (
								<p className={formSection.validationFieldMessage}>{form.errors['firstName']}</p>
							)}
							<label htmlFor="firstName">First Name</label>
							<input id="firstName" {...form.getInputProps({ name: 'firstName', type: 'text' })} />
						</div>

						<div className={formSection.field}>
							{form.errors['lastName'] && (
								<p className={formSection.validationFieldMessage}>{form.errors['lastName']}</p>
							)}
							<label htmlFor="lastName">Last Name</label>
							<input id="lastName" {...form.getInputProps({ name: 'lastName', type: 'text' })} />
						</div>
					</div>

					<div className={formSection.field}>
						{form.errors['email'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['email']}</p>
						)}
						<label htmlFor="email">Email Address</label>
						<input id="email" {...form.getInputProps({ name: 'email', type: 'text' })} />
					</div>

					<div className={formSection.phoneField}>
						{form.errors['phoneNumberPrefix'] && (
							<p className={formSection.validationFieldMessage}>
								{form.errors['phoneNumberPrefix']}
							</p>
						)}
						{form.errors['phoneNumber'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['phoneNumber']}</p>
						)}
						<label htmlFor="phoneNumberPrefix">Phone Number</label>
						<select
							id="phoneNumberPrefix"
							name="phoneNumberPrefix"
							{...form.getInputProps({ name: 'phoneNumberPrefix', type: 'text' })}
						>
							<option value="+61">+61</option>
						</select>
						<input
							id="phoneNumber"
							{...form.getInputProps({ name: 'phoneNumber', type: 'text' })}
						/>
					</div>
				</fieldset>

				<button
					className={formSection.submitButton}
					type="submit"
					disabled={form.hasErrors || form.isLoading}
				>
					Submit Listing
				</button>
			</div>
		</form.Form>
	));

	render() {
		return (
			<section className={wrapperStyle(this.state.hasSubmitted)}>
				<Helmet title="Employment">
          <meta property="og:type" content="website"/>
          <meta property="og:title" content="Advertise Employment Opportunity"/>
          <meta name="description" content="Please complete the form to submit your ad to our listing."/>
          <meta property="og:description" content="Please complete the form to submit your ad to our listing."/>
        </Helmet>
				<div className={innerWrapperStyle}>
					<div className={detailSideBarLayout.wrapper}>
						<div className={detailSideBarLayout.leftSide}>
							<div className={contentSection('#fff')}>
								{!this.state.hasSubmitted && (
									<React.Fragment>
										<h1 className={titleStyle}>Advertise Employment Opportunity</h1>

										<div className={bodyStyle}>
											<p>Please complete the form below to submit your ad to our listing.</p>
										</div>

										<this.EmploymentSubmitForm />
									</React.Fragment>
								)}
								{this.state.hasSubmitted && (
									<React.Fragment>
										<h1 className={titleStyle}>
											Thank you for submitting your employment advertisement.
										</h1>

										<div className={bodyStyle}>
											<p>The team at InsideOut will review the employment opportunity, and if appropriate will advertise it on our website as soon as possible.</p>
										</div>

										<div className={contentSection('#fff', 0)}>
											<Link className={button(pink)} to="/get-involved">
												Go to Get Involved
											</Link>
										</div>
									</React.Fragment>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
}



// WEBPACK FOOTER //
// ./src/pages/employment/submit/index.js